import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Code2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { WorkbookPreview } from '@/components/preview/WorkbookPreview';
import { HtmlPreview } from '@/components/preview/HtmlPreview';
import type { Workbook, WorkbookProfile } from '@ellie/shared';

type PreviewTab = 'react' | 'html';

interface WorkbookWithProfile extends Workbook {
  workbook_profiles: WorkbookProfile | null;
}

/**
 * Build a minimal WorkbookProfile for studio workbooks that don't have
 * a linked workbook_profiles row. Uses the profile embedded in
 * generation_metadata by the studio-generate function.
 */
function buildStudioFallbackProfile(workbook: Workbook): WorkbookProfile {
  const meta = workbook.generation_metadata as Record<string, unknown> | null;
  const sp = (meta?.studio_profile ?? {}) as Record<string, unknown>;

  return {
    id: 'studio',
    user_id: 'studio',
    display_name: (sp.display_name as string) ?? 'Studio',
    native_language: (sp.native_language as WorkbookProfile['native_language']) ?? 'en',
    target_language: (sp.target_language as WorkbookProfile['target_language']) ?? 'es',
    level: (sp.level as WorkbookProfile['level']) ?? 'b1',
    pace: (sp.pace as WorkbookProfile['pace']) ?? 'regular',
    topics: (sp.topics as WorkbookProfile['topics']) ?? [],
    learning_reason: null,
    profession: null,
    destination_city: null,
    destination_country: null,
    situations: null,
    learning_focus: null,
    shipping_name: null,
    shipping_address_line1: null,
    shipping_address_line2: null,
    shipping_city: null,
    shipping_postal_code: null,
    shipping_country: null,
    is_active: false,
    created_at: workbook.created_at,
    updated_at: workbook.updated_at,
  };
}

export function WorkbookPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [workbook, setWorkbook] = useState<WorkbookWithProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<PreviewTab>(
    searchParams.get('tab') === 'html' ? 'html' : 'react',
  );

  useEffect(() => {
    async function fetchWorkbook() {
      if (!id) return;

      const { data, error } = await supabase
        .from('workbooks')
        .select('*, workbook_profiles(*)')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Failed to fetch workbook:', error.message);
      }

      setWorkbook(data as WorkbookWithProfile | null);
      setLoading(false);
    }

    fetchWorkbook();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!workbook) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Workbook not found.</p>
      </div>
    );
  }

  // Use linked profile if available, otherwise build from studio metadata
  const profile = workbook.workbook_profiles ?? buildStudioFallbackProfile(workbook);

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to={`/workbook/${id}`}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-brand"
      >
        <ArrowLeft size={16} />
        Back to editor
      </Link>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl text-text-primary">{workbook.title}</h1>
          <p className="mt-1 text-sm text-text-muted">
            {workbook.theme} &middot; {workbook.blocks.length} blocks
            {workbook.is_studio && (
              <>
                {' '}
                &middot;{' '}
                <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                  STUDIO
                </span>
              </>
            )}
          </p>
        </div>

        {/* Preview mode tabs */}
        <div className="flex rounded-lg border border-border bg-white p-0.5">
          <button
            onClick={() => setActiveTab('react')}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'react'
                ? 'bg-brand text-white'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <BookOpen size={14} />
            Component
          </button>
          <button
            onClick={() => setActiveTab('html')}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'html'
                ? 'bg-brand text-white'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <Code2 size={14} />
            HTML Preview
          </button>
        </div>
      </div>

      {/* Preview content */}
      {activeTab === 'react' ? (
        <WorkbookPreview workbook={workbook} profile={profile} />
      ) : (
        <HtmlPreview
          workbookId={workbook.id}
          cachedHtml={workbook.rendered_html}
          cachedAt={workbook.html_rendered_at}
          cachedModel={workbook.html_render_model}
        />
      )}
    </div>
  );
}
